// HTML for the Belmont's Curse save report: summary, section tabs and each tab's content.
// Rendered as strings from the parsed save + gamedata.json; every game string goes through esc() or rich().
import { renderAreaMap, areaProgress, roomIconKind } from "./castlevania_belmonts_curse_map"
import {
  OBJECT_TYPES,
  OBJECT_TYPES_BY_KEY,
  OBJECT_GROUPS,
  objectStatusContext,
  objectStatus,
  objectLabel,
} from "./castlevania_belmonts_curse_objects"
import { esc, img } from "./game_tool/html"
import { percent } from "./game_tool/format"
import { SHOW_ALL_TAB, tabSet as kitTabSet, withShowAll } from "./game_tool/tabs"
import {
  bar,
  checkIcon,
  checkRow,
  COLLECTION_FILTERS,
  activeFilter,
  completionEntry,
  completionRow,
  completionSectionButtons,
  count,
  emptyState,
  filterEmptyText,
  filterGroup as kitFilterGroup,
  filterKeep,
  itemCard as kitItemCard,
  ownedKey,
  ownedTag as kitOwnedTag,
  panel,
  stat,
  tallyPanel,
  toggle,
} from "./game_tool/components"

// The game's ItemType enum. Charms (3) render as relic cards; clues (5) belong to arcana, so they're on the Tarot tab.
const FRAGMENTS_PER_UPGRADE = 4
const UPGRADES_PER_STAT = 6
// Not in the save or game data; matched against in-game stats screens.
const BASE_HEALTH_MANA = 100
const HEALTH_MANA_PER_UPGRADE = 20
// Upgrade heart art: [x, y, width] of each layer within the frame (96×111), matched against its art.
const HEART_SIZE = [96, 111]
const HEART_FULL = [10, 11, 76]
const HEART_PIECES = {
  hp: [[10, 11, 60], [21, 11, 61], [27, 43, 59], [38, 11, 44]],
  mp: [[10, 11, 60], [21, 11, 61], [27, 43, 59], [40, 11, 42]],
}
const ITEM_TYPES = { weapon: 1, spell: 2, charm: 3, ability: 4, clue: 5, quest: 6, outfit: 7 }
const METRIC_LABELS = {
  DestroyedCandlesMetric: "Candles destroyed",
  DistanceWalkedMetric: "Distance travelled",
  FoundTraditionalSecretZone: "Secret areas found",
}
export const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5, 3, 4]

// --- Formatting -----------------------------------------------------------

// Input action names in game text that don't read as a button; shown as the ability they trigger.
const INPUT_LABELS = { VaniaLight: "Holy Light", WhipHook: "Arcane Whip" }

// Boss fight IDs in the order they're met; fights not listed follow in area order.
const BOSS_ORDER = ["Leon", "Jeanne", "Medusa", "DeathCatacombs", "IsaacIntro", "Talos", "Brauner", "Carmilla", "Death", "Mammon", "Isaac", "SuccubusAgnes", "StLouis", "Arthur", "JeanneDArcRematch", "Doppelganger", "Dracula", "DraculaBeast"]
const ENDING_ORDER = ["Bad", "Neutral", "True"]
// Areas in story order (map tabs, completion breakdown); areas not listed follow in gamedata order.
const AREA_ORDER = [
  "World_Biome_01_Streets_Poor", "World_Biome_02_InnocentsCemetery", "World_Biome_03_Catacombs",
  "World_Biome_06_MainHall", "World_Biome_09_ClockTower", "World_Biome_08_Library", "World_Biome_05_NotreDame",
  "World_Biome_10_HallOfMirrors", "World_Biome_11_MasterQuarter", "World_Biome_12_MQBadEnding",
]
const areaRank = (world) => (AREA_ORDER.includes(world) ? AREA_ORDER.indexOf(world) : AREA_ORDER.length)
const byArea = (a, b) => areaRank(a) - areaRank(b)
// What counts toward completion besides the counters (upgrades, books, altars), in display order.
const COMPLETION_KINDS = [
  ["items", "Items picked up"], ["arcana", "Arcana absorbed"], ["virtues", "Virtues obtained"],
  ["zdoors", "Doors"], ["transitions", "Area exits found"], ["fastTravel", "Fast travel points"],
  ["zones", "Exploration zones"], ["keys", "Endings"],
]
// Kinds grouped under collapsible rows; counters (upgrades, books, altars) join the first group.
const COMPLETION_GROUPS = [
  { key: "items", label: "Upgrades and items", kinds: ["counter", "items", "arcana", "virtues"] },
  { key: "exploration", label: "Exploration", kinds: ["zones", "zdoors", "transitions", "fastTravel"] },
]
// Completion counter names (gamedata completionCounters) to their map object types.
const COUNTER_TYPES = {
  "Upgrade Health": "healthUp", "Upgrade Mana": "manaUp", "Book Atk Up": "atkBook",
  "Book Def Up": "defBook", "Experience Altar": "xpAltar",
}
// Marks map categories (and the button that selects them) that count toward completion.
const COMPLETION_MARK = `<i class="fa-solid fa-percent game-tool__bc-completion-mark" aria-hidden="true"></i>`

/** Game strings use Unity rich text (<style=...>, <input="Dash">, <i>, <br>). Convert the safe parts to HTML. */
function rich(text) {
  if (!text) return ""
  const html = esc(text.replace(/\u200b/g, ""))
    .replace(/&lt;input=(?:&quot;)?([\w/]+)(?:&quot;)?&gt;/g, (_, action) => `<kbd>${INPUT_LABELS[action] || action}</kbd>`)
    .replace(/&lt;br\s*\/?&gt;/gi, "<br>")
    .replace(/&lt;(i|b)&gt;/gi, "<em>")
    .replace(/&lt;\/(i|b)&gt;/gi, "</em>")
    .replace(/&lt;style=(?:&quot;)?\w+(?:&quot;)?&gt;/g, '<span class="game-tool__bc-hl">')
    .replace(/&lt;\/style&gt;/g, "</span>")
    .replace(/&lt;\/?[a-z][^&]*?&gt;/gi, "")
  return balanceTags(html)
}

/**
 * Game text isn't always well-formed (one clue opens <i> and never closes it). An unclosed
 * tag would swallow the rest of the page, so drop stray closers and close anything left open.
 */
function balanceTags(html) {
  const open = []
  const out = html.replace(/<(\/?)(em|span)\b[^>]*>/g, (tag, closing, name) => {
    if (!closing) {
      open.push(name)
      return tag
    }
    if (open[open.length - 1] === name) {
      open.pop()
      return tag
    }
    return ""
  })
  return out + open.reverse().map((name) => `</${name}>`).join("")
}

function plain(text) {
  return (text || "").replace(/<[^>]+>/g, "").replace(/\u200b/g, "")
}

function duration(seconds) {
  const s = Math.floor(seconds || 0)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m ${String(r).padStart(2, "0")}s`
}

function prettify(name) {
  return String(name).replace(/^World_Biome_\d+_/, "").replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2")
}

/** The shared owned mark, led by a star when `mastered` (an arcana with every task and talent done). */
function ownedTag(isEquipped, { label, mastered = false } = {}) {
  const leadHtml = mastered
    ? `<i class="fa-solid fa-star game-tool__bc-mastered" aria-hidden="true"></i><span class="visually-hidden">All Works of Mercy and Blessings done, </span>`
    : ""
  return kitOwnedTag(isEquipped, { ...(label && { label }), leadHtml })
}

/** A weapon's menu damage, with its real hit damage (the AttackWeapon stat) in parentheses. */
function weaponDamage(it) {
  if (!it?.damage) return ""
  const text = `${it.damage}${it.attack != null ? ` (${it.attack})` : ""}`
  return `<span class="game-tool__bc-damage"><span class="visually-hidden">Damage: </span>${esc(text)}</span>`
}

function plainList(items) {
  return `<ul class="game-tool__bc-plain">${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>`
}

/** Shared "Flavor text" switch; one preference covers relic lore and clue verses. */
function flavorToggle(ui) {
  return toggle("Flavor text", "showLore", !ui.hideLore, { small: true })
}

// --- Report ---------------------------------------------------------------

/**
 * Report renderer bound to one copy of the game data.
 * `ui` is the controller's view state: { fileName, debug, spoilers, filters, tab, world, zoom, adv: { on, off: Set, hideDone, bulk } }.
 */
export function createReport(G) {
  const roomIndex = {}
  for (const [worldName, world] of Object.entries(G.worlds)) {
    for (const room of world.rooms) roomIndex[room.id] = { room, worldName }
  }
  const levelByWorld = {}
  for (const level of Object.values(G.levels)) levelByWorld[level.world] = level
  const hasObjectData = Object.values(G.worlds).some((w) => w.rooms.some((r) => r.objects))
  // The Ferryman has no map art of his own; his obol stands in for him on the map.
  const mapIcons = { ...G.ui, ferryman: G.items.FerrymanCompletionKey?.icon }

  const areaName = (worldName) => (levelByWorld[worldName] ? levelByWorld[worldName].name : prettify(worldName))

  function roomLabel(room) {
    const district = room.district && G.districts[room.district]
    const area = district && district.name ? district.name : areaName(roomIndex[room.id].worldName)
    if (/SaveRoom/.test(room.id)) return `${area} · Mirror of Life`
    if (/FastTravelRoom/.test(room.id)) return `${area} · Ferryman`
    if (/Boss/.test(room.id)) return `${area} · Boss room`
    return area
  }

  /** The room's scene name in the game files, e.g. StreetsPoorV2_Room47_uid141. Shown with ?debug only. */
  const sceneName = (room) => room.id.slice(roomIndex[room.id].worldName.length + 1)

  /**
   * Background doors come in pairs that share a `zdoor` number within an area. Returns one link per
   * pair whose rooms are both on the map. Unpaired doors (boss rooms, exits) are left out.
   */
  function doorLinks(world, explored, showAll) {
    const ends = {}
    for (const room of world.rooms) {
      if (!showAll && !explored[room.id]) continue
      for (const obj of room.objects || []) {
        if (obj.c === "zdoor" && obj.zdoor !== undefined) (ends[obj.zdoor] ||= []).push(obj)
      }
    }
    return Object.values(ends).filter((pair) => pair.length === 2).map(([a, b]) => ({ a, b }))
  }

  /**
   * An elevator's stops share an x position. Returns one chain per pair of neighbouring stops among
   * the drawn objects, so a shaft only shows once the map shows both of its ends.
   */
  function elevatorChains(objects) {
    const shafts = {}
    for (const { obj } of objects) {
      if (obj.c === "elevator") (shafts[obj.x] ||= []).push(obj.y)
    }
    return Object.entries(shafts).flatMap(([x, ys]) => {
      ys.sort((a, b) => a - b)
      return ys.slice(1).map((top, i) => ({ x: Number(x), bottom: ys[i], top }))
    })
  }

  /** Exits from an area to others, once the save has them on the map (or with spoilers). */
  function mapExits(world, explored, showAll) {
    const exits = []
    for (const room of world.rooms) {
      const discovered = explored[room.id]?.discoveredBiomeTransitionsSerializable || []
      for (const obj of room.objects || []) {
        if (obj.c !== "exit" || !(showAll || discovered.includes(obj.link))) continue
        exits.push({ x: obj.x, y: obj.y, dir: obj.dir, label: `Exit to ${areaName(worldOfLevel(obj.to))}` })
      }
    }
    return exits
  }

  /** Whether the save meets a landmark step condition (a progression key, a cutscene, or all/any of them). */
  function conditionMet(s, cond) {
    if (cond.all) return cond.all.every((c) => conditionMet(s, c))
    if (cond.any) return cond.any.some((c) => conditionMet(s, c))
    if (cond.cutscene) return (s.player.watchedCutscenes || []).includes(cond.cutscene)
    return s.progressionKeys.has(cond.key) === cond.has
  }

  /**
   * The area's landmark art: each discovered landmark (all of them with spoilers) at the step the
   * save has reached, which picks its sprite.
   */
  function mapLandmarks(s, world, showAll) {
    const discovered = new Set(s.player.discoveredLandmarks || [])
    return (world.map.landmarks || [])
      .filter((l) => showAll || discovered.has(l.id))
      .map((l) => {
        const conditions = G.landmarkSteps[l.id] || []
        let step = 0
        while (step < conditions.length && conditionMet(s, conditions[step])) step++
        return l.steps[Math.min(step, l.steps.length - 1)]
      })
  }

  /** Find the room containing a saved world position. */
  function roomAt(worldName, pos) {
    const world = G.worlds[worldName]
    if (!world || !pos) return null
    const [cw, ch] = world.chunkSize
    const gx = pos.x / cw, gy = pos.y / ch
    for (const room of world.rooms) {
      for (let i = 0; i < room.chunks.length; i++) {
        if (room.chunks[i] !== "1") continue
        const x = room.x + (i % room.w), y = room.y + Math.floor(i / room.w)
        if (gx >= x && gx < x + 1 && gy >= y && gy < y + 1) return room
      }
    }
    return null
  }

  function worldOfLevel(levelId) {
    return G.levels[levelId] ? G.levels[levelId].world : null
  }

  function taskCount(state) {
    if (!state || typeof state !== "object") return null
    const n = Object.values(state).find((v) => typeof v === "number")
    return n === undefined ? null : n
  }

  /** One upgrade heart: the frame with the full heart once all its shards are in, else the collected pieces. */
  function upgradeHeart(kind, pieces) {
    const layer = (src, [x, y, w]) => src
      ? `<img src="${esc(src)}" alt="" loading="lazy" style="left:${(100 * x) / HEART_SIZE[0]}%;top:${(100 * y) / HEART_SIZE[1]}%;width:${(100 * w) / HEART_SIZE[0]}%">`
      : ""
    const layers = [img(G.ui.hpFrame)]
    if (pieces >= FRAGMENTS_PER_UPGRADE) layers.push(layer(G.ui[`${kind}Full`], HEART_FULL))
    else for (let i = 0; i < pieces; i++) layers.push(layer(G.ui[`${kind}Piece${i + 1}`], HEART_PIECES[kind][i]))
    return `<span class="game-tool__bc-heart">${layers.join("")}</span>`
  }

  /**
   * Rose's max health, max mana, attack and defence. The save has none of these, so they're worked
   * out from level and upgrades. Fragment totals come from the world's object records when available.
   */
  function roseStats(s) {
    const p = s.player
    const tally = objectTally(s)
    const line = (value, label, extra = "", note = "") =>
      `<li><span class="game-tool__bc-rose-stat"><strong>${esc(value)}</strong> ${label}</span>${extra}${note ? `<small>${note}</small>` : ""}</li>`
    // Hearts: completed, then the one in progress, then empty frames up to the game's total.
    const fragments = (kind, key, label, n) => {
      const total = tally?.[key] ? tally[key].found : n
      const upgrades = Math.floor(total / FRAGMENTS_PER_UPGRADE)
      const toNext = total % FRAGMENTS_PER_UPGRADE
      const slots = tally?.[key] ? Math.ceil(tally[key].total / FRAGMENTS_PER_UPGRADE) : UPGRADES_PER_STAT
      const hearts = Array.from({ length: Math.max(slots, upgrades) }, (_, i) =>
        upgradeHeart(kind, i < upgrades ? FRAGMENTS_PER_UPGRADE : i === upgrades ? toNext : 0))
      const max = BASE_HEALTH_MANA + HEALTH_MANA_PER_UPGRADE * upgrades
      return line(max, label,
        `<span class="game-tool__bc-hearts" role="img" aria-label="${esc(upgrades)} of ${esc(slots)} upgrades">${hearts.join("")}</span>`,
        upgrades >= slots ? "" : `${esc(toNext)} / ${FRAGMENTS_PER_UPGRADE} to next upgrade`)
    }
    const books = (label, icon, count = 0) => line((p.level || 0) + count, label, "",
      `${esc(p.level || 0)} + ${esc(count)} ${img(G.ui[icon], `${label.toLowerCase()} books`, "game-tool__bc-book-icon") || "books"}`)
    return fragments("hp", "healthUp", "Health", p.healthUpgradeFragmentCount) +
      fragments("mp", "manaUp", "Mana", p.manaUpgradeFragmentCount) +
      books("Attack", "atkBook", p.atkBonusUpgradeCount) +
      books("Defence", "defBook", p.defBonusUpgradeCount)
  }

  // --- Sections -----------------------------------------------------------

  function renderCharacter(s, ui) {
    const p = s.player
    const world = worldOfLevel(p.levelId)
    const here = roomAt(world, p.position)
    const item = (id) => (id && G.items[id]) || null
    const slot = (label, id) => {
      const it = item(id)
      // Weapons have tall card art; everything else uses its icon.
      const pic = it && (it.art || it.icon)
      return `<li>${img(pic, "", "game-tool__bc-slot-art")}` +
        `<span class="game-tool__label">${esc(label)}</span>` +
        (it ? `<strong>${esc(it.name)}</strong>${weaponDamage(it)}<p>${rich(it.description)}</p>` : `<strong class="game-tool__muted">${id ? esc(id) : "Empty"}</strong>`) +
        `</li>`
    }
    const outfit = item(p.equippedSkin)
    const cause = G.deathCauses[p.lastCauseOfDeath]
    const deathRoom = p.hasDeathResult ? roomAt(worldOfLevel(p.lastMainMapDeathLevelId), p.lastMainMapDeathPosition) : null
    return panel("Rose", `
      <p class="game-tool__bc-rose-level">Level <strong>${esc(p.level || 0)}</strong></p>
      <div class="game-tool__bc-rose-lists">
        <ul class="game-tool__bc-rose-stats">${roseStats(s)}</ul>
        <dl class="game-tool__stat-grid game-tool__stat-grid--one">
          ${stat("Play time", duration(p.playTimeInSeconds), "", G.ui.time)}
          ${stat("Location", esc(world ? areaName(world) : "Unknown"), here ? esc(roomLabel(here)) : "", G.ui.player)}
          ${stat("Last death", p.hasDeathResult ? esc(deathRoom ? roomLabel(deathRoom) : "Unknown") : "None", p.hasDeathResult ? esc(cause || "Unknown cause") : "", G.ui.death)}
          ${stat("Outfit", outfit ? esc(outfit.name) : "None", "", outfit?.icon)}
        </dl>
      </div>
      <h3 class="game-tool__subheading">Equipped</h3>
      <ul class="game-tool__bc-equipment">
        ${slot("Weapon", p.equippedWeaponItem)}
        ${slot("Spell", p.equippedSpellItem)}
        ${(p.equippedCharmItems || []).map((c, i) => slot(`Relic ${i + 1}`, c)).join("")}
      </ul>
      ${itemSubsection(s, ui, ITEM_TYPES.ability, "Abilities")}
      ${itemSubsection(s, ui, ITEM_TYPES.quest, "Quest items")}`, "", { hideTitle: true })
  }

  /** Weapons as the in-game Armoury grid: one row per weapon type, one column per tier. */
  function renderArmoury(owned, ui, equippedId) {
    const header = [1, 2, 3, 4].map((t) => `<th scope="col">Tier ${t}</th>`).join("")
    const rows = G.weaponTypes.map(([key, label, icon]) => {
      let familyOwned = false
      const cells = [1, 2, 3, 4].map((tier) => {
        const entry = Object.entries(G.items).find(([, it]) => it.weaponType === key && it.tier === tier)
        if (!entry) return "<td></td>"
        const [id, it] = entry
        const got = owned.has(id)
        if (got) familyOwned = true
        const cls = `game-tool__bc-weapon${got ? " is-owned" : ""}${tier === 4 ? " is-legendary" : ""}`
        return got || ui.spoilers
          ? `<td class="${cls}"><div class="game-tool__bc-weapon-cell">${img(it.icon, "", "game-tool__bc-weapon-icon")}<div><strong>${esc(it.name)}</strong>${got ? ownedTag(id === equippedId) : ""}${weaponDamage(it)}<p>${rich(it.description)}</p></div></div></td>`
          : `<td class="${cls} is-locked">???</td>`
      })
      let head
      if (!familyOwned && !ui.spoilers) {
        head = '<span aria-hidden="true">???</span><span class="visually-hidden">Undiscovered weapon type</span>'
      } else {
        head = icon ? `<span title="${esc(label)}">${img(icon, label, "game-tool__bc-weapon-family")}</span>` : esc(label)
      }
      return `<tr><th scope="row">${head}</th>${cells.join("")}</tr>`
    })
    return `<div class="game-tool__bc-armoury-wrap"><table class="game-tool__bc-armoury"><thead><tr><td></td>${header}</tr></thead><tbody>${rows.join("")}</tbody></table></div>`
  }

  const sortByNumber = (a, b) => (a[1].number ?? -1) - (b[1].number ?? -1)
  const arcanaNumber = (item) => G.characters[item.arcana]?.number ?? -1

  /** Name an arcana without revealing one the player hasn't met yet. */
  function arcanaLabel(s, ui, guid) {
    const c = G.characters[guid]
    if (!c) return "an unknown arcana"
    if (!c.inCodex) return `${c.name}'s own`
    const known = ui.spoilers || (s.arcanaSteps[guid] || 0) > 0
    return known ? tarotName(s, ui, c) : "an unknown arcana"
  }

  /** The step-based title change a card has reached, if any (the Doppelganger's on absorption). */
  function tarotTransition(s, c) {
    return (c.tarotSteps || []).find(([guid, step]) => (s.arcanaSteps[guid] || 0) >= step)
  }

  /** A card's title as the game shows it; the Doppelganger's stays "The Cursed" until absorbed. */
  function tarotName(s, ui, c) {
    if (ui.spoilers || !c.tarotSteps) return c.tarot || c.name
    const hit = tarotTransition(s, c)
    return hit ? hit[2] : c.tarotDefault || c.tarot || c.name
  }

  /** A character's name as the tarot shows it; Leon is "???", then "The Fallen", then "Leon". */
  function characterName(s, c) {
    if (!c.nameKeys) return c.name
    const hit = c.nameKeys.find(([key]) => s.progressionKeys.has(key))
    return hit ? hit[1] : c.nameDefault || "???"
  }

  function effectTags(item) {
    return (item.effects || [])
      .map((e) => `<span class="game-tool__bc-effect" title="${esc(plain(G.effects[e]))}">${esc(e)}</span>`)
      .join("")
  }

  /** One line of detail under a spell or ability: where it came from. */
  function itemMeta(s, ui, id) {
    const it = G.items[id]
    const parts = []
    if (it.type === ITEM_TYPES.spell || it.type === ITEM_TYPES.ability) {
      const from = arcanaLabel(s, ui, it.arcana)
      parts.push(from.endsWith("'s own") ? from : `from ${from}`)
    }
    if (it.type === ITEM_TYPES.spell) parts.push(`${it.manaCost} mana`)
    if (it.replaces && G.items[it.replaces]) parts.push(`replaces ${G.items[it.replaces].name}`)
    return parts.join(" · ")
  }

  /**
   * List rows for items. `equipped` adds the gear/check marks, led by a star where `mastered(id)`;
   * `checkbox` leads each row with an empty or checked square for found; `extra(id)` appends HTML under the description;
   * `hideDescription` drops it (clue descriptions are flavor verses); `titleFor(id)` may return a
   * merged card's two-name heading and switch (see cardSide) to use instead of the name.
   */
  function itemRows(s, ui, ids, owned, { equipped = null, mastered = null, checkbox = false, extra = null, hideDescription = false, titleFor = null } = {}) {
    return ids.map((id) => {
      const it = G.items[id]
      const side = titleFor?.(id)
      const name = side
        ? `<span class="game-tool__bc-arcana-title">${side.control}<strong>${side.titleHtml}</strong></span>`
        : `<strong>${esc(it.name)}</strong>`
      const meta = itemMeta(s, ui, id)
      const got = owned.has(id)
      const isEquipped = equipped?.has(id)
      const cls = [got ? "" : "is-missing", it.icon ? "has-icon" : "", checkbox ? "has-check" : "", isEquipped ? "is-equipped" : ""].filter(Boolean).join(" ")
      const check = checkbox ? checkIcon(got) : ""
      return `<li class="${cls}">` +
        `${check}${img(it.icon, "", "game-tool__bc-row-icon")}<div>${name}${checkbox ? `<span class="visually-hidden">, ${got ? "found" : "not found"}</span>` : ""}${equipped && got ? ownedTag(isEquipped, { mastered: !!mastered?.(id) }) : ""}${effectTags(it)}` +
        `${meta ? `<span class="game-tool__bc-meta">${esc(meta)}</span>` : ""}` +
        `${it.description && !hideDescription ? `<p class="game-tool__bc-desc">${rich(it.description)}</p>` : ""}${extra ? extra(id) : ""}</div></li>`
    }).join("")
  }

  // Unfilled "{percentage}" text marks a leftover copy of a talent the game doesn't use.
  const TALENTS_BY_ARCANA = {}
  for (const [guid, t] of Object.entries(G.arcanaTalents)) {
    if (!/\{\w+\}/.test(t.text)) (TALENTS_BY_ARCANA[t.arcana] ||= []).push([guid, t])
  }

  /** Collapsed checklist of an arcana's talents; the game shows them all once the spell is owned. */
  function talentDetails(s, arcanaGuid) {
    const own = TALENTS_BY_ARCANA[arcanaGuid] || []
    if (!own.length) return ""
    const gem = img(G.characters[arcanaGuid]?.talentGem, "", "game-tool__bc-talent-gem")
    const unlocked = new Set(s.player.arcanaUnlockedTalents || [])
    const rows = own.map(([g, t]) => checkRow(unlocked.has(g), rich(t.text), unlocked.has(g) ? "unlocked" : "locked", { lead: gem }))
    const count = own.filter(([g]) => unlocked.has(g)).length
    return `<details class="game-tool__bc-card-details" data-collapse="talents-${esc(arcanaGuid)}"><summary>Blessings ${count} / ${own.length}</summary>` +
      `<ul class="game-tool__checklist">${rows.join("")}</ul></details>`
  }

  const TASKS_BY_ARCANA = {}
  for (const [guid, t] of Object.entries(G.arcanaTasks)) (TASKS_BY_ARCANA[t.arcana] ||= []).push([guid, t])

  // Rose's card isn't in the game's tarot deck; the Doppelganger's card goes by her tarot name
  // ("The Cursed") until absorbed, so hers joins that card as its other side.
  const JOINED_BY_HOST = {}
  for (const [guid, c] of Object.entries(G.characters)) {
    if (c.inCodex || !c.tarot || !TASKS_BY_ARCANA[guid]) continue
    const host = Object.entries(G.characters).find(([, h]) => h.inCodex && h.tarotDefault === c.tarot)
    if (host) (JOINED_BY_HOST[host[0]] ||= []).push(guid)
  }
  const JOINED_CARDS = new Set(Object.values(JOINED_BY_HOST).flat())

  // A merged card's two spells (Cursed Flame / Absolution) share one Arcanas row and the card's side switch.
  const SPELL_PAIRS = Object.entries(JOINED_BY_HOST)
    .map(([host, joined]) => ({ host, cursed: G.characters[joined[0]].spell, self: G.characters[host].spell }))
    .filter((pair) => G.items[pair.cursed] && G.items[pair.self])

  /**
   * Which spell a pair's row shows, by the card's rules: The Self's only once the card has turned
   * (or with spoilers), starting on the current side. Only offers the switch when both can show.
   */
  function spellSide(s, ui, pair, visible) {
    const turned = !!tarotTransition(s, G.characters[pair.host])
    const keys = ["cursed", "self"].filter((k) => visible(pair[k]) && (k === "cursed" || ui.spoilers || turned))
    if (!keys.length) return null
    const preferred = ui.cardSides[pair.host] || (turned ? "self" : "cursed")
    const active = keys.includes(preferred) ? preferred : keys[0]
    if (keys.length < 2) return { id: pair[active] }
    const sides = { cursed: { title: G.items[pair.cursed].name }, self: { title: G.items[pair.self].name } }
    return { id: pair[active], title: cardSide(pair.host, sides, active) }
  }

  /** Collapsed checklist of an arcana's tasks (they unlock its spell's talents); all show once the spell is owned. */
  function taskDetails(s, arcanaGuid) {
    const all = TASKS_BY_ARCANA[arcanaGuid] || []
    if (!all.length) return ""
    const completed = new Set(s.player.arcanaCompletedTasks || [])
    const seen = new Set(s.player.arcanaSeenTasks || [])
    const rows = all.map(([g, t]) => {
      const done = completed.has(g)
      const n = done ? null : taskCount(s.arcanaTaskStates[g])
      const progress = n !== null && t.target ? `${n} / ${t.target}` : !done && seen.has(g) ? "In progress" : ""
      return checkRow(done, rich(t.text), done ? "done" : "not done", { trail: progress ? `<b>${esc(progress)}</b>` : "" })
    })
    const count = all.filter(([g]) => completed.has(g)).length
    return `<details class="game-tool__bc-card-details" data-collapse="tasks-${esc(arcanaGuid)}"><summary>Works of Mercy ${count} / ${all.length}</summary>` +
      `<ul class="game-tool__checklist">${rows.join("")}</ul></details>`
  }

  /** Every task done and every talent unlocked for an arcana that has any. */
  function arcanaMastered(s, arcanaGuid) {
    const tasks = TASKS_BY_ARCANA[arcanaGuid] || [], talents = TALENTS_BY_ARCANA[arcanaGuid] || []
    if (!tasks.length && !talents.length) return false
    const completed = new Set(s.player.arcanaCompletedTasks || [])
    const unlocked = new Set(s.player.arcanaUnlockedTalents || [])
    return tasks.every(([g]) => completed.has(g)) && talents.every(([g]) => unlocked.has(g))
  }

  // The completion breakdown can always list what's missing: its entries don't name anything unseen.
  const COMPLETION_FILTERS = [
    { id: "all", label: "All" },
    { id: "missing", label: "Missing", icon: "fa-circle-exclamation" },
  ]

  const collectionFilter = (ui, key, filters = COLLECTION_FILTERS) => activeFilter(filters, ui.filters[key], ui.spoilers)

  const onlyMissing = (ui) => collectionFilter(ui, "completion", COMPLETION_FILTERS).id === "missing"

  /** The shared filter group; `key` picks which ui.filters entry it drives. */
  function filterGroup(ui, key, noun, filters = COLLECTION_FILTERS) {
    return kitFilterGroup({ prefix: "bc", key, noun, filters, current: ui.filters[key], spoilers: ui.spoilers })
  }

  /** Spells in the game's order (by arcana number; Rose's own has none, so it leads, sharing a row with Absolution). */
  function renderSpells(s, ui) {
    const owned = new Set(s.player.itemsInInventory || [])
    const equipped = new Set([s.player.equippedSpellItem].filter(Boolean))
    const all = Object.entries(G.items)
      .filter(([, it]) => it.type === ITEM_TYPES.spell)
      .sort(([, a], [, b]) => arcanaNumber(a) - arcanaNumber(b))
      .map(([id]) => id)
    const have = all.filter((id) => owned.has(id)).length
    const filter = collectionFilter(ui, "spells").id
    const keep = filterKeep(filter, owned, equipped)
    const visible = (id) => keep(id) && (ui.spoilers || owned.has(id))
    const ids = []
    const titles = {}
    for (const id of all) {
      const pair = SPELL_PAIRS.find((p) => p.cursed === id || p.self === id)
      if (!pair) {
        if (visible(id)) ids.push(id)
      } else if (id === pair.cursed) {
        const side = spellSide(s, ui, pair, visible)
        if (side) { ids.push(side.id); titles[side.id] = side.title }
      }
    }
    return tallyPanel("Arcanas", have, all.length, ids.length
      ? `<ul class="game-tool__bc-item-rows game-tool__bc-item-rows--equipment">${itemRows(s, ui, ids, owned, {
        equipped,
        mastered: (id) => arcanaMastered(s, G.items[id].arcana),
        titleFor: (id) => titles[id],
        extra: (id) => (owned.has(id) || ui.spoilers ? talentDetails(s, G.items[id].arcana) + taskDetails(s, G.items[id].arcana) : ""),
      })}</ul>`
      : emptyState(filterEmptyText(filter, "arcanas")), filterGroup(ui, "spells", "arcanas"))
  }

  /** One collection card (relics, abilities) for a gamedata item. */
  function itemCard(id, owned, ui, noun, { equipped = new Set(), lore = false } = {}) {
    const it = G.items[id]
    if (!it) return ""
    return kitItemCard({
      name: it.name,
      icon: it.icon,
      descriptionHtml: rich(it.description),
      loreHtml: lore ? rich(it.lore) : "",
      found: owned.has(id),
      equipped: equipped.has(id),
      revealed: ui.spoilers,
      noun,
    })
  }

  /** Charms in the game's menu order, one card each with icon and text. */
  function renderCharms(s, ui) {
    const owned = new Set(s.player.itemsInInventory || [])
    const equipped = new Set((s.player.equippedCharmItems || []).filter(Boolean))
    const ids = (G.charmPages || []).flat()
    const have = ids.filter((id) => owned.has(id)).length
    const filter = collectionFilter(ui, "relics").id
    const cards = ids.filter(filterKeep(filter, owned, equipped))
      .map((id) => itemCard(id, owned, ui, "relic", { equipped, lore: !ui.hideLore }))
    const controls = filterGroup(ui, "relics", "relics") + flavorToggle(ui)
    return tallyPanel("Relics", have, ids.length,
      cards.length ? `<ul class="game-tool__item-grid">${cards.join("")}</ul>` : emptyState(filterEmptyText(filter, "relics")),
      controls)
  }

  /** Owned count, progress bar and item list for one ItemType. */
  const CARD_GRIDS = {
    [ITEM_TYPES.ability]: { noun: "ability", columns: "five" },
    [ITEM_TYPES.quest]: { noun: "quest item", columns: "five" },
    [ITEM_TYPES.outfit]: { noun: "outfit", columns: "three" },
  }

  function itemGroup(s, ui, type, label) {
    const owned = new Set(s.player.itemsInInventory || [])
    const all = Object.entries(G.items).filter(([, it]) => it.type === type)
    const have = all.filter(([id]) => owned.has(id))
    const shown = (ui.spoilers ? all : have).map(([id]) => id)
    let body
    if (type === ITEM_TYPES.weapon) {
      body = ownedKey() + renderArmoury(owned, ui, s.player.equippedWeaponItem)
    } else if (CARD_GRIDS[type]) {
      // Abilities follow the tarot cards they come from; quest items and outfits have no arcana and keep game order.
      const { noun, columns } = CARD_GRIDS[type]
      const equipped = new Set(type === ITEM_TYPES.outfit ? [s.player.equippedSkin].filter(Boolean) : [])
      const cards = [...all].sort(([, a], [, b]) => arcanaNumber(a) - arcanaNumber(b)).map(([id]) => itemCard(id, owned, ui, noun, { equipped }))
      body = `<ul class="game-tool__item-grid game-tool__item-grid--${columns}">${cards.join("")}</ul>`
    } else {
      body = shown.length ? `<ul class="game-tool__bc-item-rows">${itemRows(s, ui, shown, owned)}</ul>` : ""
    }
    body ||= '<p class="game-tool__muted">None yet</p>'
    return {
      have: have.length,
      total: all.length,
      body,
      count: count(`${have.length} / ${all.length}`),
      html: `${bar(have.length, all.length, label)}${body}`,
    }
  }

  function itemPanel(s, ui, type, label) {
    const group = itemGroup(s, ui, type, label)
    return panel(`${label} ${group.count}`, group.html)
  }

  function renderArmouryTab(s, ui) {
    const group = itemGroup(s, ui, ITEM_TYPES.weapon, "Armoury")
    return tallyPanel("Armoury", group.have, group.total, group.body)
  }

  function itemSubsection(s, ui, type, label) {
    const group = itemGroup(s, ui, type, label)
    return `
      <div class="game-tool__bc-item-group">
        <h3 class="game-tool__subheading">${label} ${group.count}</h3>
        ${group.html}
      </div>`
  }

  /** A face-up card's art: its full-colour version once absorbed. */
  const cardFace = (c, absorbed) => (absorbed && c.cardAbsorbed) || c.card

  /**
   * One side of a tarot card. `card` is the slot (number, stage, location); `side` is whose title,
   * portrait, art, gifts and clues it shows; a merged card's side also brings its switch and two-title heading.
   */
  function tarotCard(s, ui, owned, card, side) {
    const { guid, c, step } = card
    const { known, showArt } = side
    // Spell and ability show once owned; clues show as found, with the rest behind spoilers.
    const gifts = side.owners.flatMap((g) => [["Arcana", G.characters[g].spell], ["Ability", G.characters[g].ability]])
      .filter(([, id]) => id && G.items[id] && (owned.has(id) || ui.spoilers))
      .map(([label, id]) => {
        const it = G.items[id]
        return `<li class="${owned.has(id) ? "" : "is-missing"}">` +
          `<span class="game-tool__label">${label}</span>${img(it.icon, "", "game-tool__bc-gift-icon")}<strong>${esc(it.name)}</strong></li>`
      })
    const allClues = side.clues || []
    const clues = allClues.filter((id) => ui.spoilers || owned.has(id))
    const cluesFound = allClues.filter((id) => owned.has(id)).length
    const clueBlock = known && clues.length
      ? `<div class="game-tool__bc-card-clues"><h4 class="game-tool__label">Clues ${ui.spoilers ? `${cluesFound} / ${allClues.length}` : `found: ${cluesFound}`}</h4>` +
        `<ul class="game-tool__bc-item-rows is-compact">${itemRows(s, ui, clues, owned, { checkbox: true, hideDescription: ui.hideLore })}</ul></div>`
      : ""
    // Card art: the card once face up, its full-color version once absorbed, the card back before that.
    const art = `url('${esc(!showArt ? G.ui.cardBack : cardFace(side.char, side.absorbed))}')`
    const location = (ui.spoilers || step >= 2) && G.levels[c.location]
    const heading = `<h3>${known ? side.titleHtml || esc(side.title) : "???"}</h3>`
    // Borders follow real progress, not spoilers: dashed until discovered, accent once face up.
    const inDeck = side.char.inCodex
    const border = inDeck && step === 0 ? " is-undiscovered" : !inDeck || step >= 2 ? " is-face-up" : ""
    return `
      <article class="game-tool__bc-arcana is-step-${step}${known ? "" : " is-unknown"}${border}${art ? " has-art" : ""}"${art ? ` style="--bc-tarot-art: ${art}"` : ""}>
        <div class="game-tool__bc-arcana-top">
        <div class="game-tool__bc-arcana-head">
        <header class="game-tool__bc-arcana-header">
          <div>
            ${known && side.control ? `<div class="game-tool__bc-arcana-title">${side.control}${heading}</div>` : heading}
            <p>${known ? esc(ui.spoilers ? side.char.name : characterName(s, side.char)) : "Undiscovered"}</p>
            ${location ? `<p class="game-tool__bc-arcana-location">Found in ${esc(location.name)}</p>` : ""}
          </div>
        </header>
        </div>
        ${clueBlock}
        </div>
        ${gifts.length ? `<ul class="game-tool__bc-gifts">${gifts.join("")}</ul>` : ""}
      </article>`
  }

  /**
   * The active side of a merged card, plus its switch (on shows The Self) and a heading reading
   * "The Cursed / The Self"; only the current title is announced, the other is visual.
   */
  function cardSide(guid, sides, active) {
    const titleHtml = Object.entries(sides)
      .map(([key, side]) => `<span${key === active ? "" : ' class="is-off" aria-hidden="true"'}>${esc(side.title)}</span>`)
      .join('<span class="is-off" aria-hidden="true"> / </span>')
    const control = `<input type="checkbox" role="switch" data-setting="cardSide" data-card="${guid}" aria-label="Show ${esc(sides.self.title)}"${active === "self" ? " checked" : ""}>`
    return { ...sides[active], titleHtml, control }
  }

  function renderArcana(s, ui) {
    const owned = new Set(s.player.itemsInInventory || [])

    const entries = Object.entries(G.characters)
      .filter(([guid, c]) => (c.inCodex || (c.tarot && TASKS_BY_ARCANA[guid])) && !JOINED_CARDS.has(guid))
      .sort(sortByNumber)
    const cards = entries.map(([guid, c]) => {
      const step = s.arcanaSteps[guid] || 0
      const card = { guid, c, step }
      const known = step > 0 || ui.spoilers || !c.inCodex
      const showArt = ui.spoilers || !c.inCodex || step >= 2
      const joined = JOINED_BY_HOST[guid]
      if (!joined) {
        return tarotCard(s, ui, owned, card, { char: c, title: tarotName(s, ui, c), owners: [guid], clues: c.clues, absorbed: step === 4, known, showArt })
      }
      // A merged card has two sides: The Cursed (the joined card, always shown, plus the codex
      // entry's clues) and The Self (the codex arcana itself). Until the card turns, only The Cursed
      // shows without spoilers; otherwise the reader can switch, starting on the current side.
      const turned = !!tarotTransition(s, c)
      const sides = {
        cursed: { char: G.characters[joined[0]], title: c.tarotDefault, owners: joined, clues: c.clues, absorbed: false, known: true, showArt: true },
        // The Self is the card's absorbed form, so its face is always the absorbed art.
        self: { char: c, title: c.tarot, owners: [guid], clues: [], absorbed: true, known, showArt },
      }
      if (!ui.spoilers && !turned) return tarotCard(s, ui, owned, card, sides.cursed)
      const active = ui.cardSides[guid] || (turned ? "self" : "cursed")
      return tarotCard(s, ui, owned, card, cardSide(guid, sides, active))
    })

    const inCodex = Object.entries(G.characters).filter(([, c]) => c.inCodex)
    const absorbed = inCodex.filter(([g]) => s.arcanaSteps[g] === 4).length
    // A non-codex card with no codex card to join sits above the grid of the 15 codex arcana.
    const loose = cards.filter((_, i) => !entries[i][1].inCodex)
    const codex = cards.filter((_, i) => entries[i][1].inCodex)
    return tallyPanel("Tarot arcana absorbed", absorbed, inCodex.length, `
      ${loose.length ? `<div class="game-tool__bc-arcana-grid is-own">${loose.join("")}</div>` : ""}
      <div class="game-tool__bc-arcana-grid">${codex.join("")}</div>`, flavorToggle(ui))
  }

  // --- Advanced map objects ----------------------------------------------

  /**
   * Objects in an area's rooms with their save status. Without spoilers, only rooms the save
   * has entered are included, so nothing in unexplored rooms is revealed.
   */
  function worldObjects(s, ui, worldName) {
    const ctx = objectStatusContext(s, G)
    const out = []
    for (const room of G.worlds[worldName].rooms) {
      if (!room.objects || !(ui.spoilers || s.rooms[room.id])) continue
      for (const obj of room.objects) {
        const type = OBJECT_TYPES_BY_KEY[obj.c]
        if (type) out.push({ room, obj, type, done: objectStatus(obj, room.id, ctx) })
      }
    }
    return out
  }

  /** A category's legend icon: the game's art when it has one, else its lettered badge. */
  function typeIcon(type) {
    return type.icon && G.ui[type.icon]
      ? img(G.ui[type.icon], "", "game-tool__bc-legend-icon")
      : `<i class="game-tool__bc-badge-swatch" style="background:${type.color}" aria-hidden="true">${esc(type.glyph)}</i>`
  }

  function renderObjectFilters(objects, ui) {
    const row = (type) => {
      const mine = objects.filter((o) => o.type === type)
      if (!mine.length) return ""
      const done = mine.filter((o) => o.done === true).length
      const counts = type.completion
        ? `${COMPLETION_MARK}<span class="visually-hidden">, counts toward completion</span>`
        : ""
      return `<li${type.completion ? ` class="is-completion"` : ""}><label><input type="checkbox" data-cat="${type.key}" ${ui.adv.off.has(type.key) ? "" : "checked"}>` +
        `${typeIcon(type)}<span>${esc(type.label)}${counts}</span><small>${type.tracked ? `${done} / ${mine.length}` : mine.length}</small></label></li>`
    }
    const columns = OBJECT_GROUPS.map((group) => {
      const rows = OBJECT_TYPES.filter((t) => t.group === group.key).map(row).join("")
      return rows ? `<div class="game-tool__bc-adv-group"><h4>${esc(group.label)}</h4><ul>${rows}</ul></div>` : ""
    })
    const pressed = (mode) => `aria-pressed="${ui.adv.bulk === mode}"`
    return `
      <fieldset class="game-tool__bc-adv-fields">
        <legend class="visually-hidden">Advanced map categories</legend>
        <div class="game-tool__bc-adv-actions">
          <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-adv="all" ${pressed("all")}>All</button>
          <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-adv="none" ${pressed("none")}>None</button>
          <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small is-completion" data-adv="completion" aria-describedby="bc-adv-completion-hint" ${pressed("completion")}>${COMPLETION_MARK}Completion only</button>
          <span id="bc-adv-completion-hint" class="visually-hidden">Shows only the categories that count toward completion. Exploration zones, the biggest share, aren't map objects: unexplored map squares show where they are.</span>
          <label class="game-tool__toggle"><input type="checkbox" data-setting="hideDone" ${ui.adv.hideDone ? "checked" : ""}> Hide completed</label>
          <span class="game-tool__hint">${ui.spoilers ? "All rooms in this area." : "Rooms you've entered only. Turn on spoilers to see the whole area."}</span>
        </div>
        <div class="game-tool__bc-adv-columns">${columns.join("")}</div>
      </fieldset>`
  }

  /** Found / known / total per tracked object category across all areas; null without room object data. */
  function objectTally(s) {
    if (!hasObjectData) return null
    const ctx = objectStatusContext(s, G)
    const tally = {}
    for (const world of Object.values(G.worlds)) {
      for (const room of world.rooms) {
        const entered = !!s.rooms[room.id]
        for (const obj of room.objects || []) {
          const type = OBJECT_TYPES_BY_KEY[obj.c]
          if (!type || !type.tracked) continue
          const t = (tally[obj.c] ||= { found: 0, seen: 0, total: 0 })
          const done = objectStatus(obj, room.id, ctx) === true
          t.total++
          if (entered || done) t.seen++ // found from elsewhere (e.g. a landmark seen at a distance) counts as known
          if (done) t.found++
        }
      }
    }
    return tally
  }

  /** Progress on every tracked object category, across all areas. */
  function renderCollectibleProgress(s, ui) {
    const tally = objectTally(s)
    if (!tally) return ""
    const rows = OBJECT_TYPES.filter((type) => tally[type.key]).map((type) => {
      const t = tally[type.key]
      return `<tr><td><span class="game-tool__bc-cat">${typeIcon(type)}${esc(type.label)}</span></td><td class="is-num">${t.found}</td>` +
        `<td class="is-num">${t.seen}</td>${ui.spoilers ? `<td class="is-num">${t.total}</td>` : ""}</tr>`
    })
    return panel("Collectibles", `
      <table class="game-tool__bc-table">
        <thead><tr><th scope="col">Category</th><th scope="col" class="is-num">Found</th><th scope="col" class="is-num">Known</th>${ui.spoilers ? '<th scope="col" class="is-num">In game</th>' : ""}</tr></thead>
        <tbody>${rows.join("")}</tbody>
      </table>`)
  }

  function renderMap(s, ui) {
    const p = s.player
    const worlds = Object.keys(G.worlds).sort(byArea)
    const visited = worlds.filter((w) => G.worlds[w].rooms.some((r) => s.rooms[r.id]))
    const tabs = ui.spoilers ? worlds : visited
    if (!tabs.length) return panel("Map", '<p class="game-tool__muted">No rooms explored yet.</p>')
    const currentWorld = worldOfLevel(p.levelId)
    if (!tabs.includes(ui.world)) ui.world = currentWorld && tabs.includes(currentWorld) ? currentWorld : tabs[0]

    const world = G.worlds[ui.world]
    const markers = []
    if (currentWorld === ui.world) markers.push({ ...p.position, kind: "player", label: "Rose (save position)" })
    if (p.hasDeathResult && worldOfLevel(p.lastMainMapDeathLevelId) === ui.world) {
      const cause = G.deathCauses[p.lastCauseOfDeath]
      markers.push({ ...p.lastMainMapDeathPosition, kind: "death", label: `Last death${cause ? `: ${cause}` : ""}` })
    }
    // Advanced map: in-world objects, filtered by the category toggles.
    const objects = ui.adv.on ? worldObjects(s, ui, ui.world) : []
    const drawn = objects.filter((o) => !ui.adv.off.has(o.obj.c) && !(ui.adv.hideDone && o.done === true))
    const pr = areaProgress(world, s.rooms)
    const links = doorLinks(world, s.rooms, ui.spoilers)
    const chains = elevatorChains(drawn)
    const exits = mapExits(world, s.rooms, ui.spoilers)
    const svg = renderAreaMap(world, s.rooms, {
      showAll: ui.spoilers, labelFor: ui.debug ? (room) => `${roomLabel(room)} · ${sceneName(room)}` : roomLabel, markers, zoom: ui.zoom,
      icons: mapIcons, lastSaveRoom: p.lastUsedSaveRoomId, secrets: ui.spoilers,
      links,
      chains,
      exits,
      landmarks: mapLandmarks(s, world, ui.spoilers),
      ariaLabel: `Map of ${areaName(ui.world)}, ${percent(pr.done, pr.total)}% explored`,
      objects: drawn.map(({ obj, done, type }) => ({
        x: obj.x, y: obj.y, done,
        label: objectLabel(obj, G, ui.spoilers || done !== false) +
          (done === null ? "" : ` (${(type.states || ["done", "not yet"])[done ? 0 : 1]})`),
        icon: (done === true && type.doneIcon && G.ui[type.doneIcon]) || (type.icon && G.ui[type.icon]),
        glyph: type.glyph, color: type.color,
      })),
    })
    const banner = levelByWorld[ui.world] && levelByWorld[ui.world].banner
    // The key only lists what this map shows.
    const shownRooms = world.rooms.filter((r) => ui.spoilers || s.rooms[r.id])
    const roomIcons = new Set(shownRooms.map(roomIconKind))
    const shownMarkers = new Set(markers.map((m) => m.kind))
    const lastSaveShown = shownRooms.some((r) => r.id === p.lastUsedSaveRoomId)
    const legendIcon = (key, fallback) => (mapIcons[key]
      ? img(mapIcons[key], "", "game-tool__bc-legend-icon")
      : `<i class="game-tool__bc-swatch is-${fallback}" aria-hidden="true"></i>`)

    const playerIcon = legendIcon("player", "player")
    const deathIcon = legendIcon("death", "death")
    const deathWorld = p.hasDeathResult ? worldOfLevel(p.lastMainMapDeathLevelId) : null
    const tabButtons = tabs.map((w) => {
      const selected = w === ui.world
      const roseHere = w === currentWorld
      const diedHere = w === deathWorld
      return `<button type="button" role="tab" class="game-tool__tab" id="bc-world-tab-${esc(w)}" data-world="${esc(w)}" ` +
        `aria-controls="bc-world-panel" aria-selected="${selected}" tabindex="${selected ? 0 : -1}">` +
        `${esc(areaName(w))}${roseHere ? `${playerIcon}<span class="visually-hidden">, Rose is here</span>` : ""}` +
        `${diedHere ? `${deathIcon}<span class="visually-hidden">, last death</span>` : ""}</button>`
    })
    const zoomControls = `
      <div class="game-tool__bc-map-zoom" role="group" aria-label="Zoom">
        <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-zoom="-1" aria-label="Zoom out"${ui.zoom <= ZOOM_STEPS[0] ? " disabled" : ""}>−</button>
        <span aria-live="polite" data-zoom-label>${Math.round(ui.zoom * 100)}%</span>
        <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-zoom="1" aria-label="Zoom in"${ui.zoom >= ZOOM_STEPS.at(-1) ? " disabled" : ""}>+</button>
      </div>`

    return panel("Map", `
      <div class="game-tool__tabs game-tool__subtabs game-tool__bc-area-tabs" role="tablist" aria-label="Areas">${tabButtons.join("")}</div>
      <div id="bc-world-panel" role="tabpanel" aria-labelledby="bc-world-tab-${esc(ui.world)}">
      <div class="game-tool__bc-map-layout">
        <div class="game-tool__bc-map-main">
          <div class="game-tool__bc-map-wrap${ui.zoom > 1 ? " is-zoomed" : ""}" data-map-wrap="${esc(ui.world)}"${svg ? ' tabindex="0" role="region" aria-label="Area map"' : ""}>${svg || '<p class="game-tool__muted">Not visited yet.</p>'}</div>
          <span class="tooltip__bubble game-tool__bc-map-tip" data-map-tip aria-hidden="true"></span>
          ${hasObjectData ? `
            <div class="game-tool__bc-adv-filters">
              <div class="game-tool__bc-adv-head">
                <label class="game-tool__toggle game-tool__bc-adv-switch"><input type="checkbox" role="switch" data-setting="advancedMap" aria-describedby="bc-adv-warning" ${ui.adv.on ? "checked" : ""}> Advanced map</label>
                <small id="bc-adv-warning" class="game-tool__hint">WARNING: Spoilers may still slip through!</small>
              </div>
              ${ui.adv.on ? renderObjectFilters(objects, ui) : ""}
            </div>` : ""}
        </div>
        <aside class="game-tool__bc-map-side" aria-label="Map controls and key">
          <div class="game-tool__bc-map-tools">
            ${zoomControls}
            ${svg ? `<button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small" data-map-open aria-label="Expand map"><i class="fa-solid fa-expand" aria-hidden="true"></i></button>` : ""}
          </div>
          <div class="game-tool__bc-area-banner">${img(banner, "", "")}</div>
          <ul class="game-tool__bc-legend" aria-label="Map key">
            <li><i class="game-tool__bc-swatch is-explored" aria-hidden="true"></i>Explored</li>
            <li><i class="game-tool__bc-swatch is-seen" aria-hidden="true"></i>Seen, not explored</li>
            ${ui.spoilers ? '<li><i class="game-tool__bc-swatch is-hidden" aria-hidden="true"></i>Not visited</li>' : ""}
            ${roomIcons.has("save") ? `<li>${legendIcon("saveRoom", "save")}Mirror of Life</li>` : ""}
            ${G.ui.lastSaveRoom && lastSaveShown ? `<li>${legendIcon("lastSaveRoom", "save")}Last Mirror of Life used</li>` : ""}
            ${mapIcons.ferryman && roomIcons.has("ferryman") ? `<li>${legendIcon("ferryman")}Ferryman</li>` : ""}
            ${links.length ? '<li><i class="game-tool__bc-swatch is-link" aria-hidden="true"></i>Door connection</li>' : ""}
            ${chains.length ? '<li><i class="game-tool__bc-swatch is-chain" aria-hidden="true"></i>Elevator shaft</li>' : ""}
            ${roomIcons.has("boss") ? '<li><i class="game-tool__bc-swatch is-boss" aria-hidden="true"></i>Boss room</li>' : ""}
            ${exits.length ? '<li><i class="game-tool__bc-swatch is-exit" aria-hidden="true"></i>Exit to another area</li>' : ""}
            ${shownMarkers.has("player") ? `<li>${legendIcon("player", "player")}Save position</li>` : ""}
            ${shownMarkers.has("death") ? `<li>${legendIcon("death", "death")}Last death</li>` : ""}
          </ul>
        </aside>
      </div>
      </div>
      ${svg ? `
        <dialog class="game-tool__bc-map-dialog" data-map-dialog aria-labelledby="bc-map-dialog-title">
          <div class="game-tool__bc-map-dialog-panel">
            <div class="game-tool__bc-map-dialog-head">
              <h2 id="bc-map-dialog-title">${esc(areaName(ui.world))}</h2>
              ${zoomControls}
              <p class="game-tool__bc-map-hint">Scroll to zoom, drag to pan.</p>
              <button type="button" class="game-tool__btn game-tool__btn--ghost game-tool__btn-small game-tool__bc-map-close" data-map-close aria-label="Close map"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
            </div>
            <div class="game-tool__bc-map-dialog-body" data-map-dialog-body></div>
            <span class="tooltip__bubble game-tool__bc-map-tip" data-map-tip aria-hidden="true"></span>
          </div>
        </dialog>` : ""}`, "game-tool__bc-map-panel", { hideTitle: true })
  }

  function renderStory(s) {
    const p = s.player
    const objectives = [...new Set(p.receivedMainObjectives || [])]
    const landmarks = (p.discoveredLandmarks || []).map((g) => G.landmarks[g] || "Unknown landmark")
    // Not every landmark asset is placed in the world; count the placed ones when room data has them.
    const placed = Object.values(G.worlds).flatMap((w) => w.rooms).flatMap((r) => r.objects || []).filter((o) => o.c === "landmark")
    const landmarkTotal = placed.length || Object.keys(G.landmarks).length
    const objectiveItems = objectives.map((g) => {
      const current = g === p.currentMainObjective
      return `<li class="${current ? "is-current" : "is-done"}">${esc(G.objectives[g] || g)}` +
        `<span class="visually-hidden">${current ? " (current)" : " (done)"}</span></li>`
    }).join("")

    return panel("Story", `
      <h3 class="game-tool__subheading">Objectives</h3>
      <ol class="game-tool__bc-objectives">${objectiveItems}</ol>
      <h3 class="game-tool__subheading">Landmarks ${count(`${landmarks.length} / ${landmarkTotal}`)}</h3>
      ${landmarks.length ? plainList(landmarks) : '<p class="game-tool__muted">None yet.</p>'}`)
  }

  /** A milestone card: solid when done, dashed when not (spoilers), "???" when not (no spoilers). */
  function milestoneCard(ui, { name, variant, portrait, where, done }, { doneLabel, lockedLabel }) {
    if (!done && !ui.spoilers) {
      return `<li class="game-tool__item-card is-locked"><div class="game-tool__item-card-icon"></div><strong>???</strong><span class="visually-hidden">${esc(lockedLabel)}</span></li>`
    }
    return `<li class="game-tool__item-card${done ? "" : " is-missing"}">` +
      `<div class="game-tool__item-card-icon">${img(portrait)}</div><strong>${esc(name)}</strong>` +
      `${variant ? `<small>${esc(variant)}</small>` : ""}${where ? `<small>${esc(where)}</small>` : ""}` +
      `${done ? ownedTag(false, { label: doneLabel }) : `<span class="visually-hidden">, not ${doneLabel.toLowerCase()}</span>`}</li>`
  }

  const CHARACTER_BY_NAME_KEY = {}
  for (const [guid, c] of Object.entries(G.characters)) for (const [key] of c.nameKeys || []) CHARACTER_BY_NAME_KEY[key] ||= guid
  const FIGHT_BY_KEY = Object.fromEntries((G.bossFights || []).filter((f) => f.key).map((f) => [f.key, f]))
  // St. Louis and Brauner never get their defeat key; these flags are set when their encounters happen.
  const FIGHT_FLAGS = { "GPE St Louis Scale Progression Done": "StLouis", "GPE Brauner Statue Destroyed": "Brauner" }
  for (const [key, name] of Object.entries(G.progressionKeys)) {
    const fight = FIGHT_FLAGS[name] && (G.bossFights || []).find((f) => f.id === FIGHT_FLAGS[name])
    if (fight) FIGHT_BY_KEY[key] = fight
  }
  // Encounters rather than fights: nothing to defeat.
  const ENCOUNTER_FIGHTS = new Set(["DeathCatacombs", "StLouis", "Brauner"])
  const REMATCH_FIGHTS = new Set(["Isaac", "JeanneDArcRematch"])
  const fightVerb = (id) => (REMATCH_FIGHTS.has(id) ? "Rematch" : ENCOUNTER_FIGHTS.has(id) ? "Encountered" : "Defeated")
  const DRACULA_FIGHTS = new Set(["Dracula", "DraculaBeast"])
  const cutsceneKey = (name) => Object.keys(G.cutscenes).find((g) => G.cutscenes[g] === name)
  const DOPPELGANGER_OUTRO = cutsceneKey("Notre Dame Prophecy Self Outro")
  const NORMAL_ENDING_CUTSCENE = cutsceneKey("Master Quarters Dracula Self Phase Normal Ending")

  function bossEvent(s, fight) {
    const c = G.characters[fight.character]
    // A fought boss is known even if its name-reveal flag never got set (the Doppelganger's).
    const known = c && characterName(s, c)
    return {
      kind: "boss",
      fight: fight.id,
      character: fight.character,
      verb: fightVerb(fight.id),
      label: c ? (known === c.nameDefault ? c.name : known) : fight.name,
      art: c?.portrait,
    }
  }

  /**
   * The Doppelganger sets no story flag, only its outro cutscene. Beating it opens the True ending's
   * final fight, so it goes just before that run of Dracula events. A Neutral ending in that run
   * belongs to it unless its cutscene came before the Doppelganger's (an earlier, separate clear).
   */
  function placeDoppelganger(s, events) {
    const cutscenes = s.player.watchedCutscenes || []
    const outroAt = cutscenes.indexOf(DOPPELGANGER_OUTRO)
    const fight = (G.bossFights || []).find((f) => f.id === "Doppelganger")
    if (outroAt < 0 || !fight) return
    const trueAt = events.findIndex((e) => e.kind === "ending" && e.label === "True ending")
    if (trueAt < 0) return events.push(bossEvent(s, fight))
    const normalAt = cutscenes.indexOf(NORMAL_ENDING_CUTSCENE)
    const inFinalRun = (e) => DRACULA_FIGHTS.has(e.fight) || (e.label === "Neutral ending" && normalAt > outroAt)
    let at = trueAt
    while (at > 0 && inFinalRun(events[at - 1])) at--
    events.splice(at, 0, bossEvent(s, fight))
  }

  /**
   * The run in story-flag order: cards found (a character's first name reveal), bosses defeated
   * and endings reached. The save keeps no timestamps, so this is order only.
   */
  function runEvents(s) {
    const seen = new Set()
    const events = []
    for (const key of s.player.progressionPersistentSave || []) {
      const ending = (G.progressionKeys[key] || "").match(/^Ending Reached (\w+) Progression Key$/)
      const fight = FIGHT_BY_KEY[key]
      const guid = CHARACTER_BY_NAME_KEY[key]
      if (ending) {
        events.push({ kind: "ending", label: `${ending[1]} ending` })
      } else if (fight) {
        events.push(bossEvent(s, fight))
      } else if (guid && !seen.has(guid) && G.characters[guid].card) {
        seen.add(guid)
        const c = G.characters[guid]
        // Bright once its arcana is absorbed (its boss beaten), as on the Tarot tab.
        const card = { kind: "card", character: guid, label: tarotName(s, { spoilers: false }, c), art: cardFace(c, s.arcanaSteps[guid] === 4) }
        // A card revealed right after its own fight's flag goes first, as it does for every other boss.
        const last = events.at(-1)
        if (last?.kind === "boss" && last.character === guid) events.splice(-1, 0, card)
        else events.push(card)
      }
    }
    placeDoppelganger(s, events)
    return events
  }

  const RUN_KINDS = { card: "Card found", ending: "Reached" }

  function renderRunTimeline(s) {
    const events = runEvents(s)
    if (!events.length) return ""
    const body = (e) => `
      <div class="game-tool__bc-run-body is-${e.kind} ${e.kind === "boss" ? "is-below" : "is-above"}">
        ${e.kind === "ending" ? '<span class="game-tool__bc-run-art"><i class="fa-solid fa-flag-checkered" aria-hidden="true"></i></span>' : img(e.art, "", "game-tool__bc-run-art")}
        <span class="game-tool__bc-run-kind">${e.verb || RUN_KINDS[e.kind]}</span>
        <strong>${esc(e.label)}</strong>
      </div>`
    // A card found right before its own boss shares that boss's slot (and its boss-coloured dot).
    const slots = []
    for (let i = 0; i < events.length; i++) {
      const e = events[i], next = events[i + 1]
      if (e.kind === "card" && next?.kind === "boss" && next.character === e.character) slots.push([e, events[++i]])
      else slots.push([e])
    }
    const items = slots.map((slot) => {
      const dot = slot.at(-1).kind
      return `<li class="game-tool__bc-run-event is-${dot}${slot.some((e) => e.kind !== "boss") ? " has-above" : ""}">${slot.map(body).join("")}</li>`
    })
    return panel("Timeline", `
      <div class="game-tool__bc-run" role="region" aria-label="Timeline, scrolls sideways" tabindex="0">
        <ol class="game-tool__bc-run-track${events.some((e) => e.label === "True ending") ? "" : " is-ongoing"}">${items.join("")}</ol>
      </div>`)
  }

  /** Endings, from their "Ending Reached <kind> Progression Key"s; each also counts toward completion. */
  function renderEndings(s, ui) {
    const endings = Object.entries(G.progressionKeys)
      .map(([g, name]) => [g, (name.match(/^Ending Reached (\w+) Progression Key$/) || [])[1]])
      .filter(([, kind]) => kind)
      .sort(([, a], [, b]) => ENDING_ORDER.indexOf(a) - ENDING_ORDER.indexOf(b))
    const reached = endings.filter(([g]) => s.progressionKeys.has(g)).length
    const cards = endings.map(([g, kind]) =>
      milestoneCard(ui, { name: kind, done: s.progressionKeys.has(g) }, { doneLabel: "Reached", lockedLabel: "Unreached ending" }))
    return panel(`Endings ${count(`${reached} / ${endings.length}`)}`, `
      ${bar(reached, endings.length, "Endings reached")}
      <ul class="game-tool__item-grid game-tool__bc-boss-grid">${cards.join("")}</ul>`)
  }

  /** Boss fights as cards in story order (BOSS_ORDER). */
  function renderBosses(s, ui) {
    const saved = new Set(s.player.progressionPersistentSave || [])
    const fights = G.bossFights || []
    const rank = (f) => {
      const i = BOSS_ORDER.indexOf(f.id)
      return i < 0 ? BOSS_ORDER.length + fights.indexOf(f) : i
    }
    // Absorbing a boss's arcana also counts: the Doppelganger has no defeat key, and St. Louis
    // (the scale) and Brauner (the statue) are finished without theirs ever being set. Only for
    // single-fight characters, though: Jeanne's arcana is absorbed long before her rematch.
    const fightsPer = {}
    for (const f of fights) fightsPer[f.character] = (fightsPer[f.character] || 0) + 1
    const beaten = (f) => (f.key && saved.has(f.key)) || (fightsPer[f.character] === 1 && s.arcanaSteps[f.character] === 4)
    const fightKeys = new Set(fights.map((f) => f.key))
    // Defeat keys that no placed fight sets still show, named after the key.
    const otherBosses = [...saved].filter((g) => / Defeated$/.test(G.progressionKeys[g] || "") && !fightKeys.has(g))
    const defeated = fights.filter(beaten).length + otherBosses.length

    // Most bosses are arcana characters: use the name the tarot shows and their portrait.
    const bossOf = (f) => {
      const c = G.characters[f.character]
      const room = roomIndex[f.rooms[0]]
      return {
        name: c ? characterName(s, c) : f.name,
        variant: f.variant,
        portrait: c?.portrait,
        where: room ? areaName(room.worldName) : "",
        done: beaten(f),
      }
    }
    const labels = { doneLabel: "Defeated", lockedLabel: "Undefeated boss" }
    const cards = [
      ...[...fights].sort((a, b) => rank(a) - rank(b)).map(bossOf),
      ...otherBosses.map((g) => ({ name: G.progressionKeys[g].replace(/ Defeated$/, ""), done: true })),
    ].map((b) => milestoneCard(ui, b, labels))
    return panel(`Bosses / encounters ${count(`${defeated} / ${fights.length}`)}`, `
      ${bar(defeated, fights.length, "Bosses / encounters defeated")}
      <ul class="game-tool__item-grid game-tool__bc-boss-grid">${cards.join("")}</ul>`)
  }

  function metricsOf(p) {
    return G.metrics.map((name, i) => [name, (p.metricsValues || [])[i] ?? 0])
  }

  function renderOverview(s) {
    const p = s.player
    const metrics = metricsOf(p)
    const value = (name) => (metrics.find(([n]) => n === name) || [0, 0])[1]
    const km = (value("DistanceWalkedMetric") / 1000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    return panel("Overview", `
      <dl class="game-tool__stat-grid game-tool__stat-grid--three">
        ${stat("Current objective", esc(G.objectives[p.currentMainObjective] || "None"))}
        ${stat(METRIC_LABELS.DistanceWalkedMetric, `${km} km`)}
        ${stat(METRIC_LABELS.DestroyedCandlesMetric, esc(value("DestroyedCandlesMetric")))}
      </dl>`, "", { hideTitle: true })
  }

  /**
   * Each area's completion count split by kind, plus every entry with its room. Rooms list
   * what they contribute (gamedata `completion`); the save records what's done. The per-area sums
   * reproduce each world save's currentCompletionItemCount.
   */
  function completionBreakdown(s) {
    const p = s.player
    const owned = new Set(p.itemsInInventory || [])
    const virtues = new Set(p.obtainedVirtuesCharacters || [])
    const fastTravel = new Set(p.unlockedFastTravelLevels || [])
    const out = {}
    for (const [world, { rooms }] of Object.entries(G.worlds)) {
      const kinds = {}, entries = [], districts = {}, keysSeen = new Set()
      const repeats = { done: 0, total: 0 }
      for (const room of rooms) {
        const c = room.completion
        if (!c) continue
        const rs = s.rooms[room.id] || {}
        const has = (list, value) => (rs[list] || []).includes(value)
        const counted = rs.completionCountersSerializable || {}
        const counters = Object.fromEntries((counted.m_keys || []).map((k, i) => [k, counted.m_values[i]]))
        const add = (kind, total, done, id) => {
          done = Math.min(done, total)
          const k = kinds[kind] || (kinds[kind] = [0, 0])
          k[0] += done
          k[1] += total
          entries.push({ room, kind, id, done, total })
        }
        for (const [guid, n] of Object.entries(c.counters || {})) add(`counter:${guid}`, n, counters[guid] || 0)
        for (const id of c.items || []) add("items", 1, +owned.has(id), id)
        for (const id of c.arcana || []) add("arcana", 1, +((s.arcanaSteps[id] || 0) >= 4), id)
        for (const id of c.virtues || []) add("virtues", 1, +virtues.has(id), id)
        if (c.zones) {
          const entered = Math.min((rs.enteredZonesIds || []).length, c.zones)
          add("zones", c.zones, entered)
          const d = districts[room.district || ""] || (districts[room.district || ""] = { done: 0, total: 0, visited: false })
          d.done += entered
          d.total += c.zones
          d.visited ||= !!s.rooms[room.id]
        }
        // A door seen but not entered counts, unless it's hidden in a secret area.
        for (const id of c.zdoors || []) {
          const found = has("enteredZDoorIDs", id) ||
            (has("discoveredZDoorExplorationZoneIDs", id) && !has("hiddenInSecretZoneZDoorIDs", id))
          add("zdoors", 1, +found, id)
        }
        for (const id of c.transitions || []) add("transitions", 1, +has("discoveredBiomeTransitionsSerializable", id), id)
        // Both Dracula rooms list the Neutral and True ending keys, and the game counts every copy.
        // Show each ending once; repeats stay aside so the area still reconciles with the save.
        for (const id of c.keys || []) {
          const done = +s.progressionKeys.has(id)
          if (keysSeen.has(id)) {
            repeats.done += done
            repeats.total++
          } else {
            keysSeen.add(id)
            add("keys", 1, done, id)
          }
        }
        if (c.fastTravel) add("fastTravel", 1, +fastTravel.has(c.fastTravel), c.fastTravel)
      }
      out[world] = { kinds, entries, districts, repeats }
    }
    return out
  }

  const counterType = (kind) => OBJECT_TYPES_BY_KEY[COUNTER_TYPES[G.completionCounters[kind.slice(8)]]]

  function completionKindLabel(kind) {
    if (kind.startsWith("counter:")) return counterType(kind)?.label || prettify(G.completionCounters[kind.slice(8)] || "Collectibles")
    return COMPLETION_KINDS.find(([k]) => k === kind)?.[1] || kind
  }

  function completionKindOrder(kind) {
    return kind.startsWith("counter:")
      ? OBJECT_TYPES.indexOf(counterType(kind)) - 100
      : COMPLETION_KINDS.findIndex(([k]) => k === kind)
  }

  /** What one entry is, within its kind's list. Names stay hidden until it's done or spoilers are on. */
  function entryLabel(s, ui, e) {
    const reveal = ui.spoilers || e.done === e.total
    switch (e.kind) {
      case "zones": return `${e.done} of ${e.total} entered`
      case "items": return reveal && G.items[e.id] ? G.items[e.id].name : "An item"
      case "arcana": return `Absorb ${arcanaLabel(s, ui, e.id)}`
      case "virtues": return `Virtue of ${arcanaLabel(s, ui, e.id)}`
      case "zdoors": return `Door ${e.id}`
      case "transitions": return "Exit to another area"
      case "keys": {
        const ending = (G.progressionKeys[e.id] || "").match(/^Ending Reached (\w+) Progression Key$/)?.[1]
        return reveal && ending ? `${ending} ending` : "An ending"
      }
      case "fastTravel": return "Fast travel point"
      default: return e.total > 1 ? `${e.done} of ${e.total}` : counterType(e.kind)?.one || completionKindLabel(e.kind)
    }
  }

  /** Where an entry is: the room's name once entered (or with spoilers), plus its scene name with ?debug. */
  function entryRoom(s, ui, room) {
    const name = esc(ui.spoilers || s.rooms[room.id] ? roomLabel(room) : "A room you haven't entered")
    return ui.debug ? `${name} <code>${esc(sceneName(room))}</code>` : name
  }

  const isDone = (e) => e.done >= e.total
  // Kinds whose entries are only named by the kind itself (or by the count), so a lone entry shows just its room.
  const namedByRoom = (kind) => kind.startsWith("counter:") || ["zones", "transitions", "fastTravel"].includes(kind)

  /** One entry as a line: a checked or empty box, what it is, optionally where, then its count. */
  function entryLine(s, ui, e, where = "") {
    const done = isDone(e)
    const what = where && namedByRoom(e.kind)
      ? where
      : esc(entryLabel(s, ui, e)) + (where ? ` <span class="game-tool__muted">· ${where}</span>` : "")
    return completionEntry(done, what, { trail: `<span>${e.done} / ${e.total}</span>` })
  }

  /** One kind's entries by room: a room with one entry is a line, a room with several folds out. */
  function roomRows(s, ui, r, kind) {
    const byRoom = new Map()
    for (const e of r.entries) if (e.kind === kind) byRoom.set(e.room, [...(byRoom.get(e.room) || []), e])
    return [...byRoom].map(([room, list]) => {
      const shown = onlyMissing(ui) ? list.filter((e) => !isDone(e)) : list
      if (!shown.length) return ""
      if (list.length === 1) return entryLine(s, ui, list[0], entryRoom(s, ui, room))
      return completionRow({
        labelHtml: entryRoom(s, ui, room),
        done: list.reduce((n, e) => n + e.done, 0),
        total: list.reduce((n, e) => n + e.total, 0),
        body: `<ul class="game-tool__completion-entries">${shown.map((e) => entryLine(s, ui, e)).join("")}</ul>`,
        key: `completion-room-${r.world}-${kind}-${room.id}`,
        showBar: false,
      })
    }).join("")
  }

  /** A labelled count with its own bar; `sub` nests more rows under it, in a list of class `list`. */
  function completionKindRow(label, done, total, { sub = "", key = "", list = "game-tool__completion-kinds is-sub" } = {}) {
    return completionRow({ label, done, total, body: sub ? `<ul class="${list}">${sub}</ul>` : "", key })
  }

  /** Exploration zones split by district; districts not yet visited are left out without spoilers. */
  function districtZoneRows(r, ui) {
    const entries = Object.entries(r.districts || {})
    if (entries.length < 2) return ""
    return entries.filter(([, d]) => (d.visited || ui.spoilers) && !(onlyMissing(ui) && d.done >= d.total))
      .map(([guid, d]) => completionKindRow(G.districts[guid]?.name || areaName(r.world), d.done, d.total))
      .join("")
  }

  /**
   * An area's kinds as collapsible groups, then any kinds outside a group (endings). `kinds` is sorted
   * [kind, [done, total]]. Each kind lists its entries by room; zones split by district instead, where there are several.
   */
  function completionGroups(s, r, ui, kinds) {
    const groupOf = (kind) => COMPLETION_GROUPS.find((g) => g.kinds.includes(kind.startsWith("counter:") ? "counter" : kind))
    const hidden = (done, total) => onlyMissing(ui) && done >= total
    const row = ([kind, [d, t]]) => {
      if (hidden(d, t)) return ""
      const districts = kind === "zones" ? districtZoneRows(r, ui) : ""
      return completionKindRow(completionKindLabel(kind), d, t, districts
        ? { sub: districts, key: `completion-zones-${r.world}` }
        : { sub: roomRows(s, ui, r, kind), key: `completion-kind-${kind}-${r.world}`, list: "game-tool__completion-entries" })
    }
    const groups = COMPLETION_GROUPS.map((g) => {
      const mine = kinds.filter(([kind]) => groupOf(kind) === g)
      if (!mine.length) return ""
      const done = mine.reduce((n, [, [d]]) => n + d, 0)
      const total = mine.reduce((n, [, [, t]]) => n + t, 0)
      if (hidden(done, total)) return ""
      return completionKindRow(g.label, done, total, { sub: mine.map(row).join(""), key: `completion-${g.key}-${r.world}` })
    })
    return groups.join("") + kinds.filter(([kind]) => !groupOf(kind)).map(row).join("")
  }

  /** The game's completion %, rebuilt from per-area counts; the save doesn't store the % itself. */
  function renderCompletion(s, ui) {
    const breakdown = completionBreakdown(s)
    const rows = Object.entries(G.completion || {}).sort(([a], [b]) => byArea(a, b)).map(([world, total]) => {
      const save = s.worlds[world]
      return { world, total, done: save ? save.currentCompletionItemCount || 0 : 0, entered: !!save, ...breakdown[world] }
    })
    if (!rows.length) return ""
    const done = rows.reduce((n, r) => n + r.done, 0)
    const total = rows.reduce((n, r) => n + r.total, 0)
    // Undiscovered areas stay out of the list without spoilers; they still count toward the total.
    const areas = rows.filter((r) => r.entered || ui.spoilers).map((r) => {
      const name = areaName(r.world)
      const kinds = Object.entries(r.kinds).sort(([a], [b]) => completionKindOrder(a) - completionKindOrder(b))
      const computed = kinds.reduce((n, [, [d]]) => n + d, 0) + r.repeats.done
      // Repeated endings stay in the overall % (it matches the game's) but not in the area's own count.
      const shownDone = r.done - r.repeats.done, shownTotal = r.total - r.repeats.total
      const body = completionGroups(s, r, ui, kinds)
      return completionRow({
        label: name,
        done: shownDone,
        total: shownTotal,
        top: true,
        key: `completion-${r.world}`,
        className: r.done ? "" : "is-zero",
        body: `
          ${computed !== r.done ? `<p class="game-tool__muted">These add up to ${computed}, but the save says ${r.done}: the game may have changed how it counts.</p>` : ""}
          ${body ? `<ul class="game-tool__completion-kinds">${body}</ul>` : `<p class="game-tool__muted">Everything here is collected.</p>`}`,
      })
    })
    return panel(`Completion ${count(`${percent(done, total)}%`)}`, `
      ${bar(done, total, "Completion")}
      <div class="game-tool__collection-controls">
        ${filterGroup(ui, "completion", "completion entries", COMPLETION_FILTERS)}
        ${completionSectionButtons()}
      </div>
      <ul class="game-tool__completion">${areas.join("")}</ul>`)
  }

  /** Debug-only view (?debug): what the other tabs leave out, plus the raw save. */
  function renderDebug(s, ui) {
    const p = s.player
    const flags = (p.progressionPersistentSave || []).map((g) => G.progressionKeys[g] || g)
    const tutorials = (p.completedTutorials || []).map((g) => G.tutorials[g] || "Unknown tutorial")
    const cutscenes = (p.watchedCutscenes || []).map((g) => G.cutscenes[g] || "Unknown cutscene")
    const metrics = metricsOf(p)
    const metricLabel = (n) => METRIC_LABELS[n] || prettify(n.replace(/Metric/, "").replace(/^ObtainProgressionKey_/, "Defeated "))
    const collapse = (key, summary, body) =>
      `<details class="game-tool__details" data-collapse="${key}"><summary>${summary}</summary>${body}</details>`
    return panel("Debug", `
      <dl class="game-tool__stat-grid">
        ${stat("File", esc(ui.fileName))}
        ${stat("Format version", esc(s.version))}
        ${stat("Saved", esc(s.player.saveDate))}
        ${stat("Save ID", `<code>${esc(s.player.id)}</code>`)}
      </dl>
      ${collapse("metrics", `All tracked metrics (${metrics.length})`, `<table class="game-tool__bc-table"><tbody>` +
        metrics.map(([n, v]) => `<tr class="${v ? "" : "is-zero"}"><th scope="row">${esc(metricLabel(n))}</th><td class="is-num">${esc(Math.round(v * 100) / 100)}</td></tr>`).join("") +
        "</tbody></table>")}
      ${collapse("cutscenes", `Cutscenes watched (${cutscenes.length})`, plainList(cutscenes))}
      ${collapse("tutorials", `Tutorials completed (${tutorials.length})`, plainList(tutorials))}
      ${collapse("story-flags", `Story flags (${flags.length})`, plainList(flags))}
      <pre class="game-tool__bc-raw" tabindex="0" aria-label="Raw save JSON">${esc(JSON.stringify(s.raw, null, 2))}</pre>`)
  }

  const columns = (...parts) => `<div class="game-tool__columns">${parts.join("")}</div>`

  const TABS = [
    ["rose", "Rose", renderCharacter],
    ["map", "Map", renderMap],
    ["arcanas", "Arcanas", renderSpells],
    ["armoury", "Armoury", renderArmouryTab],
    ["relics", "Relics", renderCharms],
    ["tarot", "Tarot", renderArcana],
    ["progress", "Progress", (s, ui) => renderRunTimeline(s) + columns(
      renderCompletion(s, ui),
      renderCollectibleProgress(s, ui),
    ) + renderBosses(s, ui) + renderEndings(s, ui) + renderOverview(s)],
  ]

  const DEBUG_TAB = ["data", "Debug", renderDebug]

  function tabIds(group, ui) {
    return [SHOW_ALL_TAB, ...(ui.debug ? [...TABS, DEBUG_TAB] : TABS).map(([id]) => id)]
  }

  /**
   * A WAI-ARIA tablist plus the active tab's panel. `ui.tabs[group]` holds the selected id
   * and is reset to Show all when it isn't valid (e.g. a stale stored value). Show all leaves out
   * the debug dump.
   */
  function tabSet(group, ui, label, s, extraClass = "") {
    const bind = ([id, text, renderPanel]) => [id, text, () => renderPanel(s, ui)]
    const tabs = [...withShowAll("bc", group, TABS.map(bind)), ...(ui.debug ? [bind(DEBUG_TAB)] : [])]
    if (!tabs.some(([id]) => id === ui.tabs[group])) ui.tabs[group] = SHOW_ALL_TAB
    return kitTabSet({ prefix: "bc", group, label, tabs, selected: ui.tabs[group], extraClass })
  }

  /** The whole report for one save. Mutates `ui.tabs` / `ui.world` to valid values. */
  function render(s, ui) {
    return `
      <div class="game-tool__toolbar">
        <button type="button" class="game-tool__btn game-tool__btn--ghost" data-load-another>Load another save</button>
        ${toggle("Show spoilers", "spoilers", ui.spoilers)}
      </div>
      ${tabSet("main", ui, "Save sections", s)}`
  }

  return { render, tabIds }
}
